import { getTimeSeriesSummary } from '../getTimeSeriesSummary'
import { SummaryMode } from '../types'
import type { ChartSeries } from '../types'

/** One ranked bar: a series reduced to a single value. Label, icon, and colour
 *  are NOT carried here — those resolve from {@link ChartSeriesContext} by `id`. */
export interface RankedRow {
  id: string
  /** The value the bar plots — the series reduced per its own `summaryMode`. */
  value: number
}

/** Narrow an unknown Recharts tooltip payload to a {@link RankedRow}. The
 *  `payload[0].payload` Recharts hands the tooltip is untyped (`any`), so the
 *  tooltip guards on the fields it actually reads rather than trusting the shape. */
export function isRankedRow(value: unknown): value is RankedRow {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'value' in value &&
    typeof value.id === 'string' &&
    typeof value.value === 'number'
  )
}

/**
 * Reduce each primary series to one ranked bar, sorted by value descending
 * (largest first). Each series is summarised per its own `summaryMode` via
 * {@link getTimeSeriesSummary}: `LATEST` yields the last reading (e.g. current
 * followers), `TOTAL` the sum over the range (e.g. total posts). A consumer that
 * already holds a scalar can pass a one-point series and it plots verbatim. A
 * series with no readings is dropped rather than shown as a zero bar; comparison
 * series are ignored.
 */
export function buildRankedRows(series: ChartSeries[]): RankedRow[] {
  return series
    .filter((s) => s.kind === 'primary')
    .flatMap((s): RankedRow[] => {
      const { latest } = getTimeSeriesSummary(
        s.data,
        s.summaryMode ?? SummaryMode.LATEST,
      )
      if (latest == null) return []
      return [{ id: s.id, value: latest }]
    })
    .toSorted((a, b) => b.value - a.value)
}
